// DynamicDashboard — yeni-design outer chrome (Phase 2 rebuild).
// Widget components (StatWidget, *ChartWidget, etc.) keep their existing internals
// (Antd + recharts). Only the surrounding grid + page header + empty state move
// to yeni-design Tailwind primitives.
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { useState } from 'react';
import type { ReactElement } from 'react';
import {
  StatWidget,
  KpiWidget,
  StatSparklineWidget,
  LineChartWidget,
  BarChartWidget,
  PieChartWidget,
  DonutChartWidget,
  HBarWidget,
  TableSummaryWidget,
  FieldBreakdownWidget,
  BranchControlWidget,
} from './widgets';
import { EmptyState, PageHeader, SectionCard } from './yeni-design/primitives';
import { I } from './yeni-design/icons';
import { PeriodTabs, type DashboardPeriod } from './PeriodTabs';
import type {
  DashboardWidget,
  GridSpan,
  ReportConfig,
} from '../types/panelConfig';

dayjs.locale('tr');

interface DynamicDashboardProps {
  widgets: DashboardWidget[];
  reports: ReportConfig[];
}

// 12-col grid mapping. 1=quarter, 2=half, 3=three-quarters, 4=full.
const colSpanMap: Record<GridSpan, string> = {
  1: 'lg:col-span-3',
  2: 'lg:col-span-6',
  3: 'lg:col-span-9',
  4: 'lg:col-span-12',
};

// Backward compat: older panel configs may still return 'stat-card' before the
// API deploys the widget_type rename migration. Normalize at read time.
function normalizeWidget(widget: DashboardWidget): DashboardWidget {
  const raw = widget as unknown as { type: string };
  if (raw.type === 'stat-card') {
    return { ...(widget as object), type: 'stat' } as DashboardWidget;
  }
  return widget;
}

function renderWidget(
  widget: DashboardWidget,
  reports: ReportConfig[],
  parameters: Record<string, string>,
  period: DashboardPeriod,
): ReactElement | null {
  // branch-control resolves two source IDs (summaryReportCode + kasaReportCode)
  // from config — it does NOT use widget.reportCode. Handle it before the generic
  // sourceId guard so it never depends on widget.reportCode resolving.
  if (widget.type === 'branch-control') {
    const summary = reports.find((r) => r.code === widget.config.summaryReportCode)?.sourceId;
    const kasa = reports.find((r) => r.code === widget.config.kasaReportCode)?.sourceId;
    if (!summary || !kasa) return null;
    return <BranchControlWidget config={widget.config} summarySourceId={summary} kasaSourceId={kasa} parameters={parameters} />;
  }

  const report = reports.find((r) => r.code === widget.reportCode);
  const sourceId = report?.sourceId;
  if (!sourceId) return null;

  switch (widget.type) {
    case 'stat':
      return <StatWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'kpi':
      return <KpiWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'stat-sparkline':
      return <StatSparklineWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-line': {
      // Günlük dönemde tek günlük rapor tek nokta verir → günlük varyantı
      // (saatlik rapor) varsa ona çevir. Aylık/Yıllık'ta normal kaynak.
      const cfg = widget.config;
      if (period === 'gunluk' && cfg.gunlukReportCode) {
        const gReport = reports.find((r) => r.code === cfg.gunlukReportCode);
        if (gReport?.sourceId) {
          const gWidget = {
            ...widget,
            reportCode: cfg.gunlukReportCode,
            config: {
              ...cfg,
              categoryField: cfg.gunlukCategoryField ?? cfg.categoryField,
              valueField: cfg.gunlukValueField ?? cfg.valueField,
              title: cfg.gunlukTitle ?? cfg.title,
            },
          };
          return <LineChartWidget widget={gWidget} sourceId={gReport.sourceId} parameters={parameters} />;
        }
      }
      return <LineChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    }
    case 'chart-bar':
      return <BarChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-pie':
      return <PieChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-donut':
      return <DonutChartWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'chart-hbar':
      return <HBarWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'table-summary':
      return <TableSummaryWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
    case 'field-breakdown':
      return <FieldBreakdownWidget widget={widget} sourceId={sourceId} parameters={parameters} />;
  }
}

function defaultSpan(widget: DashboardWidget): GridSpan {
  if (widget.gridSpan) return widget.gridSpan;
  if (widget.type === 'stat' || widget.type === 'kpi' || widget.type === 'stat-sparkline') return 1;
  if (widget.type === 'table-summary') return 3;
  if (widget.type === 'branch-control') return 4;
  // chart-* and field-breakdown both default to half-width (col-6)
  return 2;
}

function widgetTitle(widget: DashboardWidget): string | null {
  if (widget.type === 'stat') return widget.config.label;
  // stat-sparkline + chart-* widgets all carry a `title` on their config.
  const cfg = widget.config as { title?: string } | null;
  return cfg?.title ?? null;
}

export function DynamicDashboard({ widgets, reports }: DynamicDashboardProps) {
  const normalized = widgets.map(normalizeWidget);
  const today = dayjs();
  const [period, setPeriod] = useState<DashboardPeriod>('gunluk');

  const dateParams = (() => {
    const end = today.format('YYYY-MM-DD');
    let start = end;
    if (period === 'aylik') start = today.startOf('month').format('YYYY-MM-DD');
    else if (period === 'yillik') start = '2026-01-01';
    return { '@BASTARIH': start, '@BITTARIH': end };
  })();

  const periodLabel = period === 'gunluk' ? 'Bugün' : period === 'aylik' ? 'Bu ay' : 'Bu yıl (01.01.2026→bugün)';

  if (normalized.length === 0) {
    return (
      <>
        <PageHeader
          title="Anasayfa"
          subtitle={`${today.format('D MMMM YYYY')} · Genel bakış`}
        />
        <EmptyState
          icon={I.Inbox}
          title="Dashboard henüz yapılandırılmamış"
          body="Panelinizi özelleştirmek için yöneticinizle iletişime geçin."
        />
      </>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between mb-1">
        <PageHeader
          title="Anasayfa"
          subtitle={`${today.format('D MMMM YYYY')} · ${periodLabel}`}
        />
        <div className="pt-1">
          <PeriodTabs value={period} onChange={setPeriod} />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-5">
        {normalized.map((widget, idx) => {
          const span = colSpanMap[defaultSpan(widget)];
          const key = `${widget.type}-${widget.reportCode}-${idx}`;
          const rendered = renderWidget(widget, reports, dateParams, period);

          // Only table-summary renders bare content → it needs SectionCard chrome.
          // Charts + field-breakdown render their own <Panel> (card + title) and
          // stat widgets render their own StatCard chrome, so they pass through
          // as-is. Wrapping a Panel-rendering widget in SectionCard double-cards it
          // (nested borders + duplicated title), so we only wrap the bare one.
          if (widget.type === 'table-summary') {
            const title = widgetTitle(widget);
            return (
              <div key={key} className={`col-span-1 ${span}`}>
                <SectionCard title={title ?? undefined} bodyClass="p-4">
                  {rendered}
                </SectionCard>
              </div>
            );
          }

          return (
            <div key={key} className={`col-span-1 sm:col-span-1 ${span}`}>
              {rendered}
            </div>
          );
        })}
      </div>
    </>
  );
}
